import {
  SpecialJobEditRequest,
  SpecialJobsCreateRequest,
} from '~/common/entities/SpecialJobs/SpecialJobRequest';
import { SpecialJobsResponse as SpecialJobResponse } from '~/common/entities/SpecialJobs/SpecialJobResponse';
import { SpecialJob } from '~/common/entities/SpecialJobs/SpecialJobsTypes';
import { useAuthenticationService } from './AuthenticationService';
import { ApiErrorCallback, ApiSuccessCallback } from './callback';
import { authenticatedApi } from '../utils/authenticatedApi';
import { Nothing } from '~/common/entities/store/types';
import { createContainer } from 'unstated-next';
import { useState } from 'react';
import _ from 'lodash';

interface EditSpecialJobValues {
  name: string;
  description: string;
  durationHours: number;
  durationMinutes: number;
  durationSeconds: number;
  amount: number;
  employeeId: string;
}

const initialData = [] as SpecialJob[];

function useSpecialJob() {
  const [specialJobs, setSpecialJobs] = useState(initialData);
  const { authenticationState } = useAuthenticationService();

  const setSpecialJobsOrdered = (specialJobs: SpecialJob[]) => {
    setSpecialJobs(
      _.sortBy(specialJobs, (specialJob) =>
        specialJob.name.toLocaleLowerCase(),
      ),
    );
  };

  const createSpecialJob = async (
    values: SpecialJobsCreateRequest,
    onSuccess?: ApiSuccessCallback<SpecialJobResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const amountAsMomentDurationTest: SpecialJobsCreateRequest = {
      name: values.name,
      description: values.description,
      duration: values.duration,
      amount: values.amount,
      employee: values.employee,
      company: values.company,
    };

    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).post<SpecialJobResponse, SpecialJobsCreateRequest>(
      '/special_job/',
      amountAsMomentDurationTest,
    );
    if (ApiError && onError) onError(ApiError);
    if (data) {
      setSpecialJobsOrdered([...specialJobs, data]);
    }
    if (data && onSuccess) onSuccess(data);
  };

  const editSpecialJob = async (
    values: EditSpecialJobValues,
    specialJob: SpecialJob,
    onSuccess?: ApiSuccessCallback<SpecialJobResponse>,
    onError?: ApiErrorCallback,
  ) => {
    const newValues: SpecialJobEditRequest = {
      name: values.name,
      description: values.description,
      duration:
        values.durationHours * 60 * 60 +
        values.durationMinutes * 60 +
        values.durationSeconds,
      amount: values.amount,
      employee: values.employeeId,
    };
    const { data, error: ApiError } = await authenticatedApi(
      authenticationState,
    ).put<SpecialJobResponse, SpecialJobEditRequest>(
      `/special_job/${specialJob.id}/`,
      newValues,
    );

    if (ApiError && onError) onError();
    if (data) {
      setSpecialJobsOrdered([...specialJobs, data]);
    }
    if (data && onSuccess) onSuccess(data);
  };

  const deleteSpecialJob = async (
    specialJob: SpecialJob,
    onSuccess?: ApiErrorCallback,
    onSpecialJobIsRunning?: () => void,
    onError?: ApiErrorCallback,
  ) => {
    const { error } = await authenticatedApi(
      authenticationState,
    ).delete<Nothing>(`/special_job/${specialJob.id}/`);

    if (error) {
      if (error.response!.status === 409 && onSpecialJobIsRunning)
        onSpecialJobIsRunning();
      if (onError) onError();
    }
    if (!error) {
      setSpecialJobsOrdered(specialJobs.filter((p) => p.id !== specialJob.id));
      if (onSuccess) onSuccess();
    }
  };

  const getSpecialJobFromServer = async (
    specialJobId: number,
    onSuccess?: ApiSuccessCallback<SpecialJob>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } = await authenticatedApi(
      authenticationState,
    ).get<SpecialJob>(`/special_job/${specialJobId}/`);
    if (error) {
      return onError ? onError(error) : undefined;
    }
    if (onSuccess) {
      onSuccess(data);
    }
    return data;
  };

  const getSpecialJobs = async (
    onSuccess?: ApiSuccessCallback<SpecialJob[]>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } =
      await authenticatedApi(authenticationState).get<SpecialJob[]>(
        '/special_jobs/',
      );
    if (error) {
      return onError ? onError(error) : undefined;
    }
    console.log(data);
    if (onSuccess) {
      onSuccess(data);
    }
    return data;
  };

  return {
    specialJobs,
    setSpecialJobs,
    createSpecialJob,
    editSpecialJob,
    deleteSpecialJob,
    getSpecialJobFromServer,
    getSpecialJobs,
  };
}

export const {
  Provider: SpecialJobProvider,
  useContainer: useSpecialJobService,
} = createContainer(useSpecialJob);
